import { type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquare, Receipt, Users } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { MoneyAmount } from "../components/MoneyAmount";
import { getInitials } from "@repo/api-hooks";
import { useAuth } from "../auth/AuthContext";
import {
  usePublicGroupInvite,
  usePublicGroupInviteExpense,
} from "../api/hooks";
import type { PublicExpense, PublicGroupView } from "../api/types";

/**
 * Read-only expense detail for guests, at
 * `/groups/:groupId/join/expenses/:expenseId`. The unauthenticated mirror of
 * {@link ExpenseDetailPage}: it renders the same hero / payers / items /
 * comments, but sourced from the public invite endpoints and stripped of every
 * mutation (no edit, delete, or comment composer). A "Sign in to join" CTA is
 * the only call to action.
 */
export function PublicExpenseDetailPage(): ReactNode {
  const { groupId, expenseId } = useParams<{
    groupId: string;
    expenseId: string;
  }>();
  // The group view supplies participant display names for payer/comment
  // attribution — the public expense only carries participant ids.
  const groupQ = usePublicGroupInvite(groupId);
  const expenseQ = usePublicGroupInviteExpense(groupId, expenseId);

  if (groupQ.isLoading || expenseQ.isLoading) {
    return <Skeleton className="m-8 h-64 max-w-3xl" />;
  }
  if (!groupQ.data || !expenseQ.data) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12 sm:px-8">
        <Card tone="ghost">
          <CardContent className="py-12 text-center text-on-surface-variant">
            This expense is unavailable. Ask the group's owner to share a fresh
            link.
          </CardContent>
        </Card>
      </div>
    );
  }

  return <PublicExpenseDetail view={groupQ.data} expense={expenseQ.data} />;
}

function PublicExpenseDetail({
  view,
  expense,
}: {
  view: PublicGroupView;
  expense: PublicExpense;
}): ReactNode {
  const { login } = useAuth();
  const total = expense.items.reduce((sum, item) => sum + item.cost, 0);
  const participantNames = new Map(
    view.participants.map((p) => [p.id, p.displayName]),
  );

  return (
    <div className="mx-auto max-w-3xl space-y-stack-lg px-6 py-12 sm:px-8">
      <div className="flex items-center justify-between gap-4">
        <Link
          to={`/groups/${view.id}/join`}
          className="inline-flex cursor-pointer items-center gap-2 text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
        >
          <ArrowLeft className="size-4" />
          Back to group
        </Link>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void login(`/groups/${view.id}/join`)}
        >
          Sign in to join
        </Button>
      </div>

      {/* Hero — high-impact summary banner, mirroring the member detail view. */}
      <section className="relative isolate overflow-hidden rounded-lg bg-primary p-8 text-on-primary [clip-path:inset(0_round_2rem)]">
        <div className="relative z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="mb-3 inline-block rounded-full bg-on-primary/15 px-3 py-1 text-label-sm">
                Expense
              </span>
              <h1 className="break-words text-headline-lg">{expense.label}</h1>
              <p className="mt-1 text-label-md text-on-primary/70">
                {new Date(
                  `${expense.expenseDate}T12:00:00`,
                ).toLocaleDateString()}
              </p>
            </div>
            <div className="grid size-12 shrink-0 place-items-center rounded-md bg-on-primary/15 backdrop-blur-md">
              <Receipt className="size-6" />
            </div>
          </div>
          <div className="mt-8">
            <p className="text-label-sm text-on-primary/60">Total amount</p>
            <MoneyAmount
              minor={total}
              currency={expense.currency}
              className="text-display"
            />
            {expense.currency !== view.currency ? (
              <p className="mt-1 text-label-md text-on-primary/70">
                <MoneyAmount
                  minor={expense.groupCharge}
                  currency={view.currency}
                />{" "}
                in group currency
              </p>
            ) : null}
          </div>
        </div>
        {/* Decorative atmospheric glow */}
        <div className="pointer-events-none absolute -right-12 -top-16 size-48 rounded-full bg-on-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-12 size-48 rounded-full bg-primary-container/40 blur-3xl" />
      </section>

      <section className="space-y-stack-md">
        <div className="flex items-center justify-between">
          <h2 className="text-headline-md">Paid by</h2>
          <span className="text-label-sm text-on-surface-variant">
            {expense.payments.length} payer
            {expense.payments.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="space-y-stack-sm">
          {expense.payments.map((p) => {
            const name = participantNames.get(p.participantId) ?? "Unknown";
            const pct = total > 0 ? Math.round((p.amount / total) * 100) : 0;
            return (
              <Card
                key={p.participantId}
                tone="ghost"
                className="border border-outline-variant/60"
              >
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid size-11 shrink-0 place-items-center rounded-full bg-primary-fixed text-label-md text-on-primary-fixed-variant">
                      {getInitials(name) || <Users className="size-5" />}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-body-md font-bold">{name}</p>
                      <p className="text-label-sm text-on-surface-variant">
                        {pct === 100 ? "Paid full amount" : `${pct}% of total`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <MoneyAmount
                      minor={p.amount}
                      currency={expense.currency}
                      className="text-headline-md text-tertiary"
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="space-y-stack-md">
        <h2 className="text-headline-md">Items</h2>
        <Card
          tone="ghost"
          className="overflow-hidden border border-outline-variant/60"
        >
          <CardContent className="divide-y divide-outline-variant/30 p-0">
            {expense.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-4 p-4"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-primary-fixed text-primary">
                    <Receipt className="size-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-body-md font-bold">
                      {item.label}
                    </p>
                    <p className="text-label-sm text-on-surface-variant">
                      Split {item.split.type.toLowerCase().replaceAll("_", " ")}{" "}
                      across {item.split.divisions.length} participant
                      {item.split.divisions.length === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <MoneyAmount
                  minor={item.cost}
                  currency={expense.currency}
                  className="shrink-0 text-headline-md"
                />
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      <PublicCommentsSection view={view} expense={expense} />
    </div>
  );
}

/** Read-only comment thread — no composer, edit, or delete for guests. */
function PublicCommentsSection({
  view,
  expense,
}: {
  view: PublicGroupView;
  expense: PublicExpense;
}): ReactNode {
  const participantNames = new Map(
    view.participants.map((p) => [p.id, p.displayName]),
  );

  return (
    <section className="space-y-stack-md">
      <div className="flex items-center gap-2">
        <MessageSquare className="size-5 text-on-surface-variant" />
        <h2 className="text-headline-md">Comments</h2>
      </div>

      {expense.comments.length === 0 ? (
        <Card tone="ghost" className="border border-outline-variant/60">
          <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
              <MessageSquare className="size-6" />
            </span>
            <p className="text-body-md text-on-surface-variant">
              No comments yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card tone="ghost" className="border border-outline-variant/60">
          <CardContent className="divide-y divide-outline-variant/30 p-0">
            {expense.comments.map((c) => {
              const authorName =
                c.createdBy === "SYSTEM"
                  ? "System"
                  : (participantNames.get(c.createdBy) ?? "Unknown");
              return (
                <div key={c.id} className="px-5 py-4">
                  <p className="text-label-sm text-on-surface-variant">
                    {authorName} · {new Date(c.createdAt).toLocaleString()}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-body-md">
                    {c.message}
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
